"use client";

// src/loginMethod.ts
function toLoginMethod(snake_case) {
  if (!snake_case) {
    return { loginMethod: "unknown" };
  }
  switch (snake_case.login_method) {
    case "password":
      return { loginMethod: "password" };
    case "magic_link":
      return { loginMethod: "magic_link" };
    case "social_sso":
      return { loginMethod: "social_sso", provider: snake_case.provider };
    case "email_confirmation_link":
      return { loginMethod: "email_confirmation_link" };
    case "saml_sso":
      return { loginMethod: "saml_sso", provider: snake_case.provider, orgId: snake_case.org_id };
    case "impersonation":
      return { loginMethod: "impersonation" };
    case "generated_from_backend_api":
      return { loginMethod: "generated_from_backend_api" };
    default:
      return { loginMethod: "unknown" };
  }
}

// src/user.ts
var UserFromToken = class _UserFromToken {
  userId;
  activeOrgId;
  orgIdToOrgMemberInfo;
  // Metadata about the user
  email;
  firstName;
  lastName;
  username;
  properties;
  loginMethod;
  // If you used our migration APIs to migrate this user from a different system,
  //   this is their original ID from that system.
  legacyUserId;
  impersonatorUserId;
  constructor(userId, email, orgIdToOrgMemberInfo, firstName, lastName, username, legacyUserId, impersonatorUserId, properties, activeOrgId, loginMethod) {
    this.userId = userId;
    this.activeOrgId = activeOrgId;
    this.orgIdToOrgMemberInfo = orgIdToOrgMemberInfo;
    this.email = email;
    this.firstName = firstName;
    this.lastName = lastName;
    this.username = username;
    this.legacyUserId = legacyUserId;
    this.impersonatorUserId = impersonatorUserId;
    this.properties = properties;
    this.loginMethod = loginMethod;
  }
  getActiveOrg() {
    if (!this.activeOrgId || !this.orgIdToOrgMemberInfo) {
      return void 0;
    }
    return this.orgIdToOrgMemberInfo[this.activeOrgId];
  }
  getActiveOrgId() {
    return this.activeOrgId;
  }
  getOrg(orgId) {
    if (!this.orgIdToOrgMemberInfo) {
      return void 0;
    }
    return this.orgIdToOrgMemberInfo[orgId];
  }
  getOrgByName(orgName) {
    if (!this.orgIdToOrgMemberInfo) {
      return void 0;
    }
    const urlSafeOrgName = orgName.toLowerCase().replace(/ /g, "-");
    for (const orgId in this.orgIdToOrgMemberInfo) {
      const orgMemberInfo = this.orgIdToOrgMemberInfo[orgId];
      if (orgMemberInfo.urlSafeOrgName === urlSafeOrgName) {
        return orgMemberInfo;
      }
    }
    return void 0;
  }
  getOrgs() {
    if (!this.orgIdToOrgMemberInfo) {
      return [];
    }
    return Object.values(this.orgIdToOrgMemberInfo);
  }
  isImpersonating() {
    return !!this.impersonatorUserId;
  }
  static fromJSON(json) {
    const obj = JSON.parse(json);
    const orgIdToOrgMemberInfo = {};
    for (const orgId in obj.orgIdToOrgMemberInfo) {
      orgIdToOrgMemberInfo[orgId] = OrgMemberInfo.fromJSON(JSON.stringify(obj.orgIdToOrgMemberInfo[orgId]));
    }
    return new _UserFromToken(
      obj.userId,
      obj.email,
      orgIdToOrgMemberInfo,
      obj.firstName,
      obj.lastName,
      obj.username,
      obj.legacyUserId,
      obj.impersonatorUserId,
      obj.properties,
      obj.activeOrgId,
      obj.loginMethod
    );
  }
  static fromJwtPayload(payload) {
    let activeOrgId;
    let orgIdToOrgMemberInfo;
    if (payload.org_member_info) {
      activeOrgId = payload.org_member_info.org_id;
      orgIdToOrgMemberInfo = toOrgIdToOrgMemberInfo({ [activeOrgId]: payload.org_member_info });
    } else {
      activeOrgId = void 0;
      orgIdToOrgMemberInfo = toOrgIdToOrgMemberInfo(payload.org_id_to_org_member_info);
    }
    const loginMethod = toLoginMethod(payload.login_method);
    return new _UserFromToken(
      payload.user_id,
      payload.email,
      orgIdToOrgMemberInfo,
      payload.first_name,
      payload.last_name,
      payload.username,
      payload.legacy_user_id,
      payload.impersonator_user_id,
      payload.properties,
      activeOrgId,
      loginMethod
    );
  }
};
var OrgMemberInfo = class _OrgMemberInfo {
  orgId;
  orgName;
  orgMetadata;
  urlSafeOrgName;
  orgRoleStructure;
  userAssignedRole;
  userInheritedRolesPlusCurrentRole;
  userPermissions;
  userAssignedAdditionalRoles;
  constructor(orgId, orgName, orgMetadata, urlSafeOrgName, userAssignedRole, userInheritedRolesPlusCurrentRole, userPermissions, orgRoleStructure, userAssignedAdditionalRoles) {
    this.orgId = orgId;
    this.orgName = orgName;
    this.orgMetadata = orgMetadata;
    this.urlSafeOrgName = urlSafeOrgName;
    this.orgRoleStructure = orgRoleStructure;
    this.userAssignedRole = userAssignedRole;
    this.userInheritedRolesPlusCurrentRole = userInheritedRolesPlusCurrentRole;
    this.userPermissions = userPermissions;
    this.userAssignedAdditionalRoles = userAssignedAdditionalRoles;
  }
  // validation methods
  isRole(role) {
    if (this.orgRoleStructure === "multi_role" /* MultiRole */) {
      return this.userAssignedRole === role || this.userAssignedAdditionalRoles.includes(role);
    } else {
      return this.userAssignedRole === role;
    }
  }
  isAtLeastRole(role) {
    if (this.orgRoleStructure === "multi_role" /* MultiRole */) {
      return this.userAssignedRole === role || this.userAssignedAdditionalRoles.includes(role);
    } else {
      return this.userInheritedRolesPlusCurrentRole.includes(role);
    }
  }
  hasPermission(permission) {
    return this.userPermissions.includes(permission);
  }
  hasAllPermissions(permissions) {
    return permissions.every((permission) => this.hasPermission(permission));
  }
  static fromJSON(json) {
    const obj = JSON.parse(json);
    return new _OrgMemberInfo(
      obj.orgId,
      obj.orgName,
      obj.orgMetadata,
      obj.urlSafeOrgName,
      obj.userAssignedRole,
      obj.userInheritedRolesPlusCurrentRole,
      obj.userPermissions,
      obj.orgRoleStructure,
      obj.userAssignedAdditionalRoles
    );
  }
  // getters for the private fields
  get assignedRole() {
    return this.userAssignedRole;
  }
  get assignedRoles() {
    if (this.orgRoleStructure === "multi_role" /* MultiRole */) {
      return this.userAssignedAdditionalRoles.concat(this.userAssignedRole);
    } else {
      return [this.userAssignedRole];
    }
  }
  get inheritedRolesPlusCurrentRole() {
    if (this.orgRoleStructure === "multi_role" /* MultiRole */) {
      return this.userAssignedAdditionalRoles.concat(this.userAssignedRole);
    } else {
      return this.userInheritedRolesPlusCurrentRole;
    }
  }
  get permissions() {
    return this.userPermissions;
  }
};
function toOrgIdToOrgMemberInfo(snake_case) {
  if (snake_case === void 0) {
    return void 0;
  }
  const camelCase = {};
  for (const key of Object.keys(snake_case)) {
    const snakeCaseValue = snake_case[key];
    if (snakeCaseValue) {
      camelCase[key] = new OrgMemberInfo(
        snakeCaseValue.org_id,
        snakeCaseValue.org_name,
        snakeCaseValue.org_metadata,
        snakeCaseValue.url_safe_org_name,
        snakeCaseValue.user_role,
        snakeCaseValue.inherited_user_roles_plus_current_role,
        snakeCaseValue.user_permissions,
        snakeCaseValue.org_role_structure,
        snakeCaseValue.additional_roles
      );
    }
  }
  return camelCase;
}

// src/client/AuthContext.tsx
import React, { useCallback, useEffect, useReducer } from "react";

// src/client/utils.ts
var USER_INFO_KEY = "__PROPEL_AUTH_USER_INFO";
function hasWindow() {
  return typeof window !== "undefined";
}
function saveUserToLocalStorage(user) {
  if (user) {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(user));
  } else {
    localStorage.setItem(USER_INFO_KEY, "{}");
  }
}
function doesLocalStorageMatch(newValue, user) {
  if (!newValue) {
    return false;
  } else if (!user) {
    return newValue === "{}";
  }
  const parsed = JSON.parse(newValue);
  if (!parsed) {
    return false;
  }
  return isEqual(parsed, jsonSerialize(user));
}
function isEqual(a, b) {
  if (typeof a !== typeof b) {
    return false;
  } else if (a === null || b === null) {
    return a === b;
  }
  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }
  if (Array.isArray(a)) {
    const aArray = a;
    const bArray = b;
    if (aArray.length !== bArray.length) {
      return false;
    }
    for (let i = 0; i < aArray.length; i++) {
      if (!isEqual(aArray[i], bArray[i])) {
        return false;
      }
    }
    return true;
  }
  if (typeof a === "object") {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) {
      return false;
    }
    for (const key of aKeys) {
      if (!isEqual(a[key], b[key])) {
        return false;
      }
    }
    return true;
  } else {
    return a === b;
  }
}
function jsonSerialize(userFromToken) {
  return JSON.parse(JSON.stringify(userFromToken));
}

// src/client/AuthContext.tsx
import { reload } from "vike/client/router";

// src/client/useUser.tsx
import { useContext } from "react";
var User = class {
  userId;
  email;
  emailConfirmed;
  hasPassword;
  username;
  firstName;
  lastName;
  pictureUrl;
  orgIdToOrgMemberInfo;
  activeOrgId;
  mfaEnabled;
  canCreateOrgs;
  updatePasswordRequired;
  createdAt;
  lastActiveAt;
  properties;
  legacyUserId;
  impersonatorUserId;
  constructor({
    userId,
    email,
    emailConfirmed,
    hasPassword,
    username,
    firstName,
    lastName,
    pictureUrl,
    orgIdToOrgMemberInfo,
    activeOrgId,
    mfaEnabled,
    canCreateOrgs,
    updatePasswordRequired,
    createdAt,
    lastActiveAt,
    legacyUserId,
    properties,
    impersonatorUserId
  }) {
    this.userId = userId;
    this.email = email;
    this.emailConfirmed = emailConfirmed;
    this.hasPassword = hasPassword;
    this.username = username;
    this.firstName = firstName;
    this.lastName = lastName;
    this.pictureUrl = pictureUrl;
    this.orgIdToOrgMemberInfo = orgIdToOrgMemberInfo;
    this.activeOrgId = activeOrgId;
    this.mfaEnabled = mfaEnabled;
    this.canCreateOrgs = canCreateOrgs;
    this.updatePasswordRequired = updatePasswordRequired;
    this.createdAt = createdAt;
    this.lastActiveAt = lastActiveAt;
    this.legacyUserId = legacyUserId;
    this.properties = properties;
    this.impersonatorUserId = impersonatorUserId;
  }
  getActiveOrg() {
    if (!this.activeOrgId) {
      return void 0;
    }
    return this.getOrg(this.activeOrgId);
  }
  getActiveOrgId() {
    return this.activeOrgId;
  }
  getOrg(orgId) {
    return this.orgIdToOrgMemberInfo?.[orgId];
  }
  getOrgByName(orgName) {
    if (!this.orgIdToOrgMemberInfo) {
      return void 0;
    }
    const urlSafeOrgName = orgName.toLowerCase().replace(/ /g, "-");
    for (const orgId in this.orgIdToOrgMemberInfo) {
      const orgMemberInfo = this.orgIdToOrgMemberInfo[orgId];
      if (orgMemberInfo.urlSafeOrgName === urlSafeOrgName) {
        return orgMemberInfo;
      }
    }
    return void 0;
  }
  getOrgs() {
    if (!this.orgIdToOrgMemberInfo) {
      return [];
    }
    return Object.values(this.orgIdToOrgMemberInfo);
  }
  isImpersonating() {
    return !!this.impersonatorUserId;
  }
};
function useUser() {
  const context = useContext(AuthContext);
  if (context === void 0) {
    throw new Error("useUser must be used within an AuthProvider");
  }
  const { loading, authInfo } = context;
  if (loading) {
    return {
      loading: true,
      isLoggedIn: void 0,
      user: void 0,
      accessToken: void 0,
      setActiveOrg: void 0
    };
  } else if (authInfo?.user) {
    return {
      loading: false,
      isLoggedIn: true,
      user: authInfo.user,
      accessToken: authInfo.accessToken,
      setActiveOrg: context.setActiveOrg
    };
  } else {
    return {
      loading: false,
      isLoggedIn: false,
      user: void 0,
      accessToken: void 0,
      setActiveOrg: void 0
    };
  }
}

// src/client/AuthContext.tsx
var AuthContext = React.createContext(void 0);
var initialAuthState = {
  loading: true,
  authInfo: {
    user: void 0,
    accessToken: void 0
    // expiresAtSeconds: undefined,
  },
  authChangeDetected: false
};
function authStateReducer(_state, action) {
  const newUserForEqualityChecking = { ...action.user, lastActiveAt: void 0 };
  const existingUserForEqualityChecking = { ..._state.authInfo.user, lastActiveAt: void 0 };
  const authChangeDetected = !_state.loading && !isEqual(newUserForEqualityChecking, existingUserForEqualityChecking);
  if (!action.user) {
    return {
      loading: false,
      authInfo: {
        user: void 0,
        accessToken: void 0
      },
      authChangeDetected
    };
  }
  if (_state.loading) {
    return {
      loading: false,
      authInfo: {
        user: action.user,
        accessToken: action.accessToken
      },
      authChangeDetected
    };
  }
  return {
    loading: false,
    authInfo: {
      user: action.user,
      accessToken: action.accessToken
    },
    authChangeDetected
  };
}
var AuthProvider = (props) => {
  const [authState, dispatchInner] = useReducer(authStateReducer, initialAuthState);
  const reloadOnAuthChange = props.reloadOnAuthChange ?? true;
  const dispatch = useCallback(
    (action) => {
      dispatchInner(action);
      saveUserToLocalStorage(action.user);
    },
    [dispatchInner]
  );
  useEffect(() => {
    ;
    (async () => {
      if (reloadOnAuthChange && authState.authChangeDetected) {
        await reload();
      }
    })();
  }, [authState.authChangeDetected, reloadOnAuthChange]);
  useEffect(() => {
    let didCancel = false;
    async function refreshAuthInfo2() {
      const action = await apiGetUserInfo();
      if (!didCancel && !action.error) {
        dispatch(action);
      }
    }
    refreshAuthInfo2();
    return () => {
      didCancel = true;
    };
  }, []);
  useEffect(() => {
    let didCancel = false;
    let retryTimer = void 0;
    function clearAndSetRetryTimer() {
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      retryTimer = setTimeout(refreshToken, 30 * 1e3);
    }
    async function refreshToken() {
      const action = await apiGetUserInfo();
      if (didCancel) {
        return;
      }
      if (!action.error) {
        dispatch(action);
      } else if (action.error === "unexpected") {
        clearAndSetRetryTimer();
      }
    }
    async function onStorageEvent(event) {
      if (event.key === USER_INFO_KEY && !doesLocalStorageMatch(event.newValue, authState.authInfo.user)) {
        await refreshToken();
      }
    }
    const interval = setInterval(refreshToken, 5 * 60 * 1e3);
    if (hasWindow()) {
      window.addEventListener("storage", onStorageEvent);
      window.addEventListener("online", refreshToken);
      window.addEventListener("focus", refreshToken);
    }
    return () => {
      didCancel = true;
      clearInterval(interval);
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      if (hasWindow()) {
        window.removeEventListener("storage", onStorageEvent);
        window.removeEventListener("online", refreshToken);
        window.removeEventListener("focus", refreshToken);
      }
    };
  }, [dispatch, authState.authInfo.user]);
  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });
    dispatch({ user: void 0, accessToken: void 0 });
  }, [dispatch]);
  const getLoginPageUrl = (opts) => {
    if (opts?.postLoginRedirectPath) {
      return `/api/auth/login?return_to_path=${encodeURIComponent(opts.postLoginRedirectPath)}`;
    }
    return "/api/auth/login";
  };
  const getSignupPageUrl = (opts) => {
    if (opts?.postSignupRedirectPath) {
      return `/api/auth/signup?return_to_path=${encodeURIComponent(opts.postSignupRedirectPath)}`;
    }
    return "/api/auth/signup";
  };
  const getAccountPageUrl = useCallback(
    (opts) => {
      return addReturnToPath(`${props.authUrl}/account`, opts?.redirectBackToUrl);
    },
    [props.authUrl]
  );
  const getOrgPageUrl = useCallback(
    (orgId, opts) => {
      if (orgId) {
        return addReturnToPath(`${props.authUrl}/org?id=${orgId}`, opts?.redirectBackToUrl);
      }
      return addReturnToPath(`${props.authUrl}/org`, opts?.redirectBackToUrl);
    },
    [props.authUrl]
  );
  const getOrgSettingsPageUrl = useCallback(
    (orgId, opts) => {
      if (orgId) {
        return addReturnToPath(`${props.authUrl}/org/settings/${orgId}`, opts?.redirectBackToUrl);
      }
      return addReturnToPath(`${props.authUrl}/org/settings`, opts?.redirectBackToUrl);
    },
    [props.authUrl]
  );
  const getCreateOrgPageUrl = useCallback(
    (opts) => {
      return addReturnToPath(`${props.authUrl}/create_org`, opts?.redirectBackToUrl);
    },
    [props.authUrl]
  );
  const getSetupSAMLPageUrl = useCallback(
    (orgId, opts) => {
      return addReturnToPath(`${props.authUrl}/saml?id=${orgId}`, opts?.redirectBackToUrl);
    },
    [props.authUrl]
  );
  const getOrgApiKeysPageUrl = useCallback(
    (orgId, opts) => {
      if (orgId) {
        return addReturnToPath(`${props.authUrl}/org/api_keys/${orgId}`, opts?.redirectBackToUrl);
      }
      return addReturnToPath(`${props.authUrl}/org/api_keys`, opts?.redirectBackToUrl);
    },
    [props.authUrl]
  );
  const redirectTo = (url) => {
    window.location.href = url;
  };
  const redirectToLoginPage = (opts) => redirectTo(getLoginPageUrl(opts));
  const redirectToSignupPage = (opts) => redirectTo(getSignupPageUrl(opts));
  const redirectToAccountPage = (opts) => redirectTo(getAccountPageUrl(opts));
  const redirectToOrgPage = (orgId, opts) => redirectTo(getOrgPageUrl(orgId, opts));
  const redirectToOrgSettingsPage = (orgId, opts) => redirectTo(getOrgSettingsPageUrl(orgId, opts));
  const redirectToCreateOrgPage = (opts) => redirectTo(getCreateOrgPageUrl(opts));
  const redirectToSetupSAMLPage = (orgId, opts) => redirectTo(getSetupSAMLPageUrl(orgId, opts));
  const redirectToOrgApiKeysPage = (orgId, opts) => redirectTo(getOrgApiKeysPageUrl(orgId, opts));
  const refreshAuthInfo = useCallback(async () => {
    const action = await apiGetUserInfo();
    if (action.error) {
      throw new Error("Failed to refresh token");
    }
    dispatch(action);
    return action.user;
  }, [dispatch]);
  const setActiveOrg = useCallback(
    async (orgId) => {
      const action = await apiPostSetActiveOrg(orgId);
      if (action.error === "not_in_org") {
        return void 0;
      }
      dispatch(action);
      return action.user;
    },
    [dispatch]
  );
  const { defaultDisplayWhileLoading, defaultDisplayIfLoggedOut } = props;
  const value = {
    loading: authState.loading,
    authInfo: authState.authInfo,
    logout,
    defaultDisplayWhileLoading,
    defaultDisplayIfLoggedOut,
    redirectToLoginPage,
    redirectToSignupPage,
    redirectToAccountPage,
    redirectToOrgPage,
    redirectToOrgSettingsPage,
    redirectToCreateOrgPage,
    redirectToSetupSAMLPage,
    redirectToOrgApiKeysPage,
    getLoginPageUrl,
    getSignupPageUrl,
    getAccountPageUrl,
    getOrgPageUrl,
    getOrgSettingsPageUrl,
    getCreateOrgPageUrl,
    getOrgApiKeysPageUrl,
    getSetupSAMLPageUrl,
    refreshAuthInfo,
    setActiveOrg
  };
  return /* @__PURE__ */ React.createElement(AuthContext.Provider, { value }, props.children);
};
async function apiGetUserInfo() {
  try {
    const userInfoResponse = await fetch("/api/auth/userinfo", {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });
    if (userInfoResponse.ok) {
      const { userinfo, accessToken, impersonatorUserId, activeOrgId } = await userInfoResponse.json();
      const user = new User({
        userId: userinfo.user_id,
        email: userinfo.email,
        emailConfirmed: userinfo.email_confirmed,
        hasPassword: userinfo.has_password,
        username: userinfo.username,
        firstName: userinfo.first_name,
        lastName: userinfo.last_name,
        pictureUrl: userinfo.picture_url,
        orgIdToOrgMemberInfo: toOrgIdToOrgMemberInfo(userinfo.org_id_to_org_info),
        activeOrgId,
        mfaEnabled: userinfo.mfa_enabled,
        canCreateOrgs: userinfo.can_create_orgs,
        updatePasswordRequired: userinfo.update_password_required,
        createdAt: userinfo.created_at,
        lastActiveAt: userinfo.last_active_at,
        properties: userinfo.properties,
        impersonatorUserId
      });
      return { user, accessToken, error: void 0 };
    }
    if (userInfoResponse.status === 401) {
      return { user: void 0, accessToken: void 0, error: void 0 };
    }
    console.info("Failed to refresh token", userInfoResponse);
    return { error: "unexpected" };
  } catch (e) {
    console.info("Failed to refresh token", e);
    return { error: "unexpected" };
  }
}
async function apiPostSetActiveOrg(orgId) {
  try {
    const queryParams = new URLSearchParams({ active_org_id: orgId }).toString();
    const url = `/api/auth/set-active-org?${queryParams}`;
    const userInfoResponse = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    });
    if (userInfoResponse.ok) {
      const { userinfo, accessToken, impersonatorUserId, activeOrgId } = await userInfoResponse.json();
      const user = new User({
        userId: userinfo.user_id,
        email: userinfo.email,
        emailConfirmed: userinfo.email_confirmed,
        hasPassword: userinfo.has_password,
        username: userinfo.username,
        firstName: userinfo.first_name,
        lastName: userinfo.last_name,
        pictureUrl: userinfo.picture_url,
        orgIdToOrgMemberInfo: toOrgIdToOrgMemberInfo(userinfo.org_id_to_org_info),
        activeOrgId,
        mfaEnabled: userinfo.mfa_enabled,
        canCreateOrgs: userinfo.can_create_orgs,
        updatePasswordRequired: userinfo.update_password_required,
        createdAt: userinfo.created_at,
        lastActiveAt: userinfo.last_active_at,
        properties: userinfo.properties,
        impersonatorUserId
      });
      return { user, accessToken, error: void 0 };
    }
    if (userInfoResponse.status === 401) {
      return { error: "not_in_org" };
    }
    console.info("Failed to set active org", userInfoResponse);
  } catch (e) {
    console.info("Failed to set active org", e);
  }
  throw new Error("Failed to set active org");
}
var encodeBase64 = (str) => {
  const encode = window ? window.btoa : btoa;
  return encode(str);
};
var addReturnToPath = (url, returnToPath) => {
  if (!returnToPath) {
    return url;
  }
  const qs = new URLSearchParams();
  qs.set("rt", encodeBase64(returnToPath));
  if (url.includes("?")) {
    return `${url}&${qs.toString()}`;
  }
  return `${url}?${qs.toString()}`;
};

// src/client/RequiredAuthProvider.tsx
import React4 from "react";

// src/client/withRequiredAuthInfo.tsx
import hoistNonReactStatics from "hoist-non-react-statics";
import React3, { useContext as useContext3 } from "react";

// src/client/useRedirectFunctions.tsx
import React2, { useContext as useContext2, useEffect as useEffect2 } from "react";
function useRedirectFunctions() {
  const context = useContext2(AuthContext);
  if (context === void 0) {
    throw new Error("useRedirectFunctions must be used within an AuthProvider or RequiredAuthProvider");
  }
  const {
    redirectToAccountPage,
    redirectToSignupPage,
    redirectToLoginPage,
    redirectToOrgPage,
    redirectToOrgSettingsPage,
    redirectToCreateOrgPage,
    redirectToSetupSAMLPage,
    redirectToOrgApiKeysPage
  } = context;
  return {
    redirectToSignupPage,
    redirectToLoginPage,
    redirectToAccountPage,
    redirectToOrgPage,
    redirectToOrgSettingsPage,
    redirectToCreateOrgPage,
    redirectToSetupSAMLPage,
    redirectToOrgApiKeysPage
  };
}
function RedirectToSignup({ children }) {
  const { redirectToSignupPage } = useRedirectFunctions();
  useEffect2(() => {
    redirectToSignupPage();
  }, []);
  return /* @__PURE__ */ React2.createElement(React2.Fragment, null, children);
}
function RedirectToLogin({ children, postLoginRedirectPath }) {
  const { redirectToLoginPage } = useRedirectFunctions();
  useEffect2(() => {
    if (postLoginRedirectPath) {
      redirectToLoginPage({ postLoginRedirectPath });
    } else {
      redirectToLoginPage();
    }
  }, []);
  return /* @__PURE__ */ React2.createElement(React2.Fragment, null, children);
}

// src/client/withRequiredAuthInfo.tsx
function withRequiredAuthInfo(Component, args) {
  const displayName = `withRequiredAuthInfo(${Component.displayName || Component.name || "Component"})`;
  const WithRequiredAuthInfoWrapper = (props) => {
    const context = useContext3(AuthContext);
    if (context === void 0) {
      throw new Error("withRequiredAuthInfo must be used within an AuthProvider or RequiredAuthProvider");
    }
    const {
      loading,
      authInfo,
      defaultDisplayIfLoggedOut,
      defaultDisplayWhileLoading,
      refreshAuthInfo
      // tokens
    } = context;
    function displayLoading() {
      if (args?.displayWhileLoading) {
        return args.displayWhileLoading;
      }
      if (defaultDisplayWhileLoading) {
        return defaultDisplayWhileLoading;
      }
      return /* @__PURE__ */ React3.createElement(React3.Fragment, null);
    }
    function displayLoggedOut() {
      if (args?.displayIfLoggedOut) {
        return args.displayIfLoggedOut;
      }
      if (defaultDisplayIfLoggedOut) {
        return defaultDisplayIfLoggedOut;
      }
      return /* @__PURE__ */ React3.createElement(RedirectToLogin, null);
    }
    if (loading) {
      return displayLoading();
    }
    if (authInfo.user && authInfo.accessToken) {
      const loggedInProps = {
        ...props,
        accessToken: authInfo.accessToken,
        isLoggedIn: !!authInfo.accessToken,
        // orgHelper: authInfo.orgHelper,
        // accessHelper: authInfo.accessHelper,
        user: authInfo.user,
        // userClass: authInfo.userClass,
        isImpersonating: !!authInfo.impersonatorUserId,
        impersonatorUserId: authInfo.impersonatorUserId,
        refreshAuthInfo
        // tokens,
        // accessTokenExpiresAtSeconds: authInfo.expiresAtSeconds,
      };
      return /* @__PURE__ */ React3.createElement(Component, { ...loggedInProps });
    }
    return displayLoggedOut();
  };
  WithRequiredAuthInfoWrapper.displayName = displayName;
  WithRequiredAuthInfoWrapper.WrappedComponent = Component;
  return hoistNonReactStatics(WithRequiredAuthInfoWrapper, Component);
}

// src/client/RequiredAuthProvider.tsx
var RequiredAuthWrappedComponent = withRequiredAuthInfo(
  ({ children }) => /* @__PURE__ */ React4.createElement(React4.Fragment, null, children)
);
var RequiredAuthProvider = (props) => {
  const { children, displayIfLoggedOut, displayWhileLoading, ...sharedProps } = props;
  return /* @__PURE__ */ React4.createElement(
    AuthProvider,
    {
      ...sharedProps,
      defaultDisplayIfLoggedOut: displayIfLoggedOut,
      defaultDisplayWhileLoading: displayWhileLoading
    },
    /* @__PURE__ */ React4.createElement(RequiredAuthWrappedComponent, null, children)
  );
};

// src/client/useLogoutFunction.ts
import { useContext as useContext4 } from "react";
function useLogoutFunction() {
  const context = useContext4(AuthContext);
  if (context === void 0) {
    throw new Error("useLogoutFunction must be used within an AuthProvider");
  }
  const { logout } = context;
  return logout;
}
export {
  AuthProvider,
  OrgMemberInfo,
  RedirectToLogin,
  RedirectToSignup,
  RequiredAuthProvider,
  User,
  UserFromToken,
  useLogoutFunction,
  useRedirectFunctions,
  useUser
};
//# sourceMappingURL=index.js.map